/*
  <complementary-products>
  --------------------------------------------------------------------------
  Powers the complementary products block in the cart drawer
  (snippets/cart-complementary-products.liquid).

  Behaviour:
   - When the cart drawer opens (and after adding from this block), it reads the
     current cart and, for every product in it, fetches recommendations from the
     Product Recommendations API (JSON).
   - intent=complementary first (the
     shopify--discovery--product_recommendation.complementary_products metafield);
     if a product has none, it falls back to intent=related for that product.
   - Results are flattened in cart order, deduped, and any product already in the
     cart is removed. The block hides itself when there is nothing to show.
   - "Add" posts to /cart/add.json via the theme's CartFunctions (global.js), then
     re-renders the drawer line items / totals / bubble and refreshes the block.

  CartFunctions and <ajax-cart> are looked up lazily, so this script is
  order-independent with respect to global.js.
*/
if (!customElements.get('complementary-products')) {
  const formatMoney = (cents) => '€' + (cents / 100).toFixed(2).replace('.', ',');

  const escapeHtml = (value) =>
    String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

  const cdnImage = (url, width) => {
    if (!url) return '';
    const absolute = url.indexOf('//') === 0 ? 'https:' + url : url;
    return absolute + (absolute.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  };

  class ComplementaryProducts extends HTMLElement {
    constructor() {
      super();
      this.scroller = this.querySelector('.comp-scroller');
      this.recUrl = this.dataset.recommendationsUrl;
      this.intent = this.dataset.intent || 'complementary';
      this.fallbackIntent = this.dataset.fallbackIntent || '';
      this.limit = parseInt(this.dataset.limit, 10) || 8;
      this.isLoading = false;

      this.addEventListener('click', this.onClick.bind(this));
      this.bindDrawerOpeners();

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.load());
      } else {
        this.load();
      }
    }

    bindDrawerOpeners() {
      document.querySelectorAll('.cartToggler').forEach((opener) => {
        // Refresh once the drawer has opened and the cart has settled.
        opener.addEventListener('click', () => {
          setTimeout(() => {
            this.adjustFooterSpacing();
            this.load();
          }, 300);
        });
      });
    }

    // The cart footer is position:fixed, so it overlaps the bottom of the
    // scrollable area. Pad the scroll container by the footer's real height
    // so nothing (cart info or these cards) hides behind it.
    adjustFooterSpacing() {
      const wrapper = document.querySelector('.cartFormWrapper');
      const footer = document.querySelector('.ajax-cart-footer');
      if (!wrapper || !footer) return;
      const height = footer.offsetHeight;
      if (height) wrapper.style.paddingBottom = height + 8 + 'px';
    }

    /* ---------------------------------------------------------------- add */

    onClick(event) {
      const button = event.target.closest('[data-comp-add]');
      if (!button) return;
      event.preventDefault();
      this.addToCart(button);
    }

    async addToCart(button) {
      const card = button.closest('[data-comp-card]');
      const select = card && card.querySelector('.comp-variant');
      const variantId = select ? select.value : card && card.dataset.defaultVariant;
      if (!variantId || button.classList.contains('is-loading')) return;
      if (typeof CartFunctions === 'undefined') {
        console.warn('[complementary-products] CartFunctions is not available');
        return;
      }

      const cartFunctions = new CartFunctions();
      button.classList.add('is-loading');

      try {
        await cartFunctions.addItem(variantId, 1);
        await this.refreshDrawer(cartFunctions);
        // The added product is now in the cart, so load() drops it automatically.
        await this.load();
      } catch (error) {
        console.error('[complementary-products] add to cart failed', error);
        button.classList.remove('is-loading');
      }
    }

    /* ----------------------------------------------------- load + render */

    async load() {
      if (this.isLoading || typeof CartFunctions === 'undefined') return;
      this.isLoading = true;

      try {
        const cart = await new CartFunctions().getCart();
        const items = cart.items || [];
        const cartProductIds = new Set(items.map((item) => item.product_id));

        if (!cartProductIds.size) {
          this.render([]);
          return;
        }

        const groups = await Promise.all(
          [...cartProductIds].map((id) => this.fetchForProduct(id))
        );

        const seen = new Set();
        const products = [];
        for (const group of groups) {
          for (const product of group) {
            if (cartProductIds.has(product.id) || seen.has(product.id)) continue;
            if (product.available === false) continue;
            seen.add(product.id);
            products.push(product);
            if (products.length >= this.limit) break;
          }
          if (products.length >= this.limit) break;
        }

        this.render(products);
      } catch (error) {
        console.error('[complementary-products] load failed', error);
      } finally {
        this.isLoading = false;
      }
    }

    async fetchForProduct(productId) {
      let products = await this.fetchProducts(productId, this.intent);
      if (!products.length && this.fallbackIntent) {
        products = await this.fetchProducts(productId, this.fallbackIntent);
      }
      return products;
    }

    async fetchProducts(productId, intent) {
      const url =
        `${this.recUrl}.json?product_id=${productId}` +
        `&intent=${encodeURIComponent(intent)}&limit=${this.limit}`;

      try {
        const response = await fetch(url);
        if (!response.ok) return [];
        const data = await response.json();
        return data.products || [];
      } catch (error) {
        console.error('[complementary-products] fetch failed', error);
        return [];
      }
    }

    render(products) {
      if (!products.length) {
        this.scroller.innerHTML = '';
        this.classList.add('none');
      } else {
        this.scroller.innerHTML = products.map((p) => this.cardHtml(p)).join('');
        this.classList.remove('none');
      }
      this.adjustFooterSpacing();
    }

    cardHtml(product) {
      const variants = product.variants || [];
      const defaultVariant = variants.find((v) => v.available) || variants[0];
      const defaultId = defaultVariant ? defaultVariant.id : '';
      const hasOptions = variants.length > 1;

      const image = product.featured_image
        ? `<img class="absolute top-0 left-0 w-p100 h-p100 object-cover object-center"
                src="${cdnImage(product.featured_image, 280)}"
                alt="${escapeHtml(product.title)}" loading="lazy">`
        : '';

      const onSale = product.compare_at_price && product.compare_at_price > product.price;
      const price = onSale
        ? `<s class="color-gray">${formatMoney(product.compare_at_price)}</s><span>${formatMoney(product.price)}</span>`
        : `<span>${formatMoney(product.price)}</span>`;

      const options = hasOptions
        ? `<select class="comp-variant w-p100 mrg-pt5 border-1-solid border-black pfDin txt-12 cl-black"
                   aria-label="Επιλογή για ${escapeHtml(product.title)}">
             ${variants
          .map(
            (v) =>
              `<option value="${v.id}"${v.available ? '' : ' disabled'}${v.id === defaultId ? ' selected' : ''
              }>${escapeHtml(v.title)}${v.available ? '' : ' – Εξαντλήθηκε'}</option>`
          )
          .join('')}
           </select>`
        : '';

      return `
        <div class="comp-card flex flex-column flex-shrink-0 relative"
             data-comp-card data-product-id="${product.id}" data-default-variant="${defaultId}">
          <a class="no-decoration" href="${escapeHtml(product.url)}">
            <div class="portrait-130 relative">${image}</div>
            <h4 class="comp-card-title ivy-display txt-14 cl-black mrg-pt5">${escapeHtml(product.title)}</h4>
          </a>
          <div class="comp-card-price color-black pfDin">${price}</div>
          ${options}
          <button type="button"
                  class="comp-add w-p100 mrg-pt5 pdg-pv2 border-1-solid border-black bg-white pointer pfDin txt-12 cl-black"
                  data-comp-add
                  aria-label="Προσθήκη ${escapeHtml(product.title)} στο καλάθι"
                  ${defaultId ? '' : 'disabled'}>
            <span class="comp-add-label bold">Προσθήκη</span>
            <span class="comp-add-spinner none">…</span>
          </button>
        </div>`;
    }

    /* -------------------------------------------------- drawer refresh */

    // Delegate to the cart's own repaint so the drawer is rebuilt exactly one way
    // (line indexes, split BOGO lines, discount copy, totals and empty state).
    async refreshDrawer(cartFunctions) {
      const ajaxCart = document.querySelector('ajax-cart');
      if (!ajaxCart || typeof ajaxCart.renderCart !== 'function') return;
      ajaxCart.renderCart(await cartFunctions.getCart());
    }
  }

  customElements.define('complementary-products', ComplementaryProducts);
}
